import { useSyncExternalStore } from 'react';
import { getThemeVersion, onThemeChange } from './themes';
import { mixColours, rgbToHex, tokenColour, type TokenColour } from './colour';
import { cssVariable } from './tokens';

const fromRoot = () => {
  const style = getComputedStyle(document.documentElement);
  return (name: string) => style.getPropertyValue(cssVariable(name)).trim() || undefined;
};

// A colour as CSS rgb()/rgba(), black where there is none
function cssOf(colour: TokenColour | null, alpha = 1): string {
  if (!colour) return '#000000';
  const [r, g, b] = colour.rgb.map(c => Math.round(c));
  const opacity = colour.alpha * alpha;
  return opacity < 1 ? `rgba(${r}, ${g}, ${b}, ${Number(opacity.toFixed(3))})` : `rgb(${r}, ${g}, ${b})`;
}

/**
 * A theme token's colour as CSS a canvas, three.js or an SVG image can take, for drawing that cannot read CSS variables:
 * `themeColour('lureo-aqua')` is the current theme's aqua. Derived tokens work too.
 */
export function themeColour(token: string, alpha = 1): string {
  if (typeof document === 'undefined') return '#000000';
  return cssOf(tokenColour(token, fromRoot()), alpha);
}

// How far a pressed or hovered axis darkens into the shade, the same for every axis
const PRESSED = 0.15;

/** A theme colour pressed, as a gizmo's axis under the pointer: 15% of the way into the shade. */
export function themePressedColour(token: string): string {
  if (typeof document === 'undefined') return '#000000';
  const valueOf = fromRoot();
  const colour = tokenColour(token, valueOf);
  const shade = tokenColour('lureo-shade', valueOf);
  return cssOf(colour && shade ? mixColours(colour, shade, PRESSED) : colour);
}

/** A theme token's colour as #rrggbb, for APIs that only take hex (Google Maps styles). */
export function themeHex(token: string): string {
  if (typeof document === 'undefined') return '#000000';
  const colour = tokenColour(token, fromRoot());
  return colour ? rgbToHex(colour.rgb) : '#000000';
}

/** The theme's interface font (its sans), else `fallback`, for text drawn on a canvas or into an SVG image, or measured there. */
export const themeFont = (fallback: string) =>
  (typeof document === 'undefined' ? '' : getComputedStyle(document.documentElement).getPropertyValue(cssVariable('lureo-font-ui')).trim()) || fallback;

/** A number that goes up with every change to the theme or its tokens: for components that redraw their canvas or 3D colours with the theme. */
export const useThemeVersion = () => useSyncExternalStore(onThemeChange, getThemeVersion);
